import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import type { Team } from '@/types/entities'

interface TeamActionsProps {
    team: Team
    /** Absent for a role without teams.update: the entry is not offered. */
    onEdit?: (team: Team) => void
    /** Absent for a role without teams.delete. */
    onDelete?: (team: Team) => void
    onView: (team: Team) => void
}

function TeamActions({ team, onEdit, onDelete, onView }: TeamActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                    >
                        <span className="sr-only">{t('teams.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(team)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('teams.viewTeam')}
                    </DropdownMenuItem>
                    {onEdit && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onEdit(team)
                                setOpen(false)
                            }}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            {t('teams.editTeam')}
                        </DropdownMenuItem>
                    )}
                    {onDelete && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onDelete(team)
                                setOpen(false)
                            }}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('teams.deleteTeam')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createTeamColumns(
    onEdit: ((team: Team) => void) | undefined,
    onDelete: ((team: Team) => void) | undefined,
    onView: (team: Team) => void,
    t: (key: string) => string
): ColumnDef<Team>[] {
    return [
        {
            accessorKey: 'teamName',
            header: t('teams.teamName'),
            size: 300,
            minSize: 200,
            maxSize: 500,
        },
        {
            accessorKey: 'description',
            header: t('teams.description'),
            size: 400,
            minSize: 250,
            maxSize: 600,
            cell: ({ row }) => {
                const description = row.getValue('description') as string | null
                return (
                    <div className="text-sm">
                        {description || '-'}
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: 80,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const team = row.original
                return (
                    <TeamActions
                        team={team}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                    />
                )
            },
        },
    ]
}
